// @flow
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'material-design-icons/iconfont/material-icons.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

/* ─────────────────────────────────────────────
   IndexedDB helper (replaces localStorage for binary data)
   DB: rdpms_help  |  stores: manual_pdfs, install_pdfs, videos, meta
───────────────────────────────────────────── */
const DB_NAME = 'rdpms_help';
const DB_VERSION = 1;
const STORES = ['manual_pdfs', 'install_pdfs', 'videos', 'meta'];

function openDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
            const db = e.target.result;
            STORES.forEach((s) => {
                if (!db.objectStoreNames.contains(s)) db.createObjectStore(s);
            });
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function idbGet(store, key) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(store, 'readonly');
        const req = tx.objectStore(store).get(key);
        req.onsuccess = () => resolve(req.result ?? null);
        req.onerror = () => reject(req.error);
    });
}

async function idbSet(store, key, value) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        const req = tx.objectStore(store).put(value, key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

async function idbDel(store, key) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        const req = tx.objectStore(store).delete(key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
    });
}

async function idbGetAll(store) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(store, 'readonly');
        const results = [];
        const keys = [];
        tx.objectStore(store).openCursor().onsuccess = (e) => {
            const cursor = e.target.result;
            if (cursor) {
                keys.push(cursor.key);
                results.push(cursor.value);
                cursor.continue();
            } else {
                resolve(results.map((v, i) => ({ key: keys[i], value: v })));
            }
        };
        tx.onerror = () => reject(tx.error);
    });
}

// Convert File → ArrayBuffer (IndexedDB can store binary natively — no base64 bloat)
const fileToBuffer = (file) =>
    new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsArrayBuffer(file);
    });

// Read actual video duration from a blob/object URL → returns "MM:SS" string
const getVideoDuration = (blobUrl) =>
    new Promise((resolve) => {
        const vid = document.createElement('video');
        vid.preload = 'metadata';
        vid.onloadedmetadata = () => {
            const secs = Math.floor(vid.duration);
            const m = Math.floor(secs / 60)
                .toString()
                .padStart(2, '0');
            const s = (secs % 60).toString().padStart(2, '0');
            URL.revokeObjectURL(vid.src); // only revoke this temp copy
            resolve(`${m}:${s}`);
        };
        vid.onerror = () => resolve(null); // fallback: keep existing duration
        vid.src = blobUrl;
    });

// Create a blob: URL from stored ArrayBuffer — works as iframe src / video src
function bufferToUrl(buffer, mimeType) {
    const blob = new Blob([buffer], { type: mimeType });
    return URL.createObjectURL(blob);
}

/* ─────────────────────────────────────────────
   Lightweight JSON meta store (small strings only — still uses IDB for consistency)
───────────────────────────────────────────── */
const MetaStore = {
    get: async (key, fallback) => {
        const val = await idbGet('meta', key);
        return val !== null ? val : fallback;
    },
    set: async (key, value) => idbSet('meta', key, value),
};

/* ─────────────────────────────────────────────
   STATIC DATA
───────────────────────────────────────────── */
const sections = [
    { id: 'manual', label: 'User Manual', icon: 'bi-book' },
    { id: 'videos', label: 'Training Videos', icon: 'bi-play-circle' },
    { id: 'installation', label: 'Installation & Commissioning', icon: 'bi-tools' },
    { id: 'troubleshooting', label: 'Troubleshooting Guide', icon: 'bi-wrench-adjustable' },
    { id: 'faq', label: 'FAQ', icon: 'bi-patch-question' },
];

const defaultVideos = [
    {
        id: 1,
        title: 'System Assembly',
        duration: '12:34',
        desc: 'Step-by-step guide to assembling RDPMS hardware components.',
    },
    {
        id: 2,
        title: 'Sensor Calibration',
        duration: '08:47',
        desc: 'Calibrating sensors for accurate readings and optimal performance.',
    },
    {
        id: 3,
        title: 'Hardware Integration',
        duration: '15:20',
        desc: 'Connecting and integrating all hardware modules into the system.',
    },
    {
        id: 4,
        title: 'RDPMS Software Usage',
        duration: '22:05',
        desc: 'Complete walkthrough of the RDPMS software interface and features.',
    },
    {
        id: 5,
        title: "Do's & Don'ts",
        duration: '06:15',
        desc: 'Best practices and common mistakes to avoid during operation.',
    },
    {
        id: 6,
        title: 'Network Restoration',
        duration: '09:33',
        desc: 'Restoring network connectivity after an outage or failure.',
    },
    {
        id: 7,
        title: 'IoT Channel Reconfiguration',
        duration: '11:48',
        desc: 'Reconfiguring IoT channels for different monitoring requirements.',
    },
    {
        id: 8,
        title: 'Troubleshooting',
        duration: '18:22',
        desc: 'Diagnosing and resolving the most common system issues.',
    },
].map((v) => ({ ...v, videoUrl: null, fileName: null, mimeType: null }));

const issues = [
    {
        issue: 'Device not connecting to network',
        severity: 'High',
        severityClass: 'danger',
        steps: [
            'Check router/switch is powered on',
            'Verify IP configuration in Network Settings',
            'Ping the gateway address',
            'Restart the IoT communication module',
            'If persists, use Network Restoration procedure',
        ],
    },
    {
        issue: 'Sensor readings showing incorrect values',
        severity: 'Medium',
        severityClass: 'warning',
        steps: [
            'Run auto-calibration from the Calibration menu',
            'Check sensor wiring connections',
            'Verify sensor configuration matches the physical type',
            'Replace sensor if calibration fails repeatedly',
        ],
    },
    {
        issue: 'Dashboard not loading data',
        severity: 'Medium',
        severityClass: 'warning',
        steps: [
            'Confirm backend service is running',
            'Check API endpoint configuration',
            'Clear browser cache and refresh',
            'Review browser console for errors',
        ],
    },
    {
        issue: 'Login authentication failure',
        severity: 'Low',
        severityClass: 'success',
        steps: [
            'Verify credentials are correct',
            'Check Caps Lock is off',
            'Contact admin for password reset',
            'Check auth service logs',
        ],
    },
    {
        issue: 'Data export not generating',
        severity: 'Low',
        severityClass: 'success',
        steps: [
            'Ensure a date range is selected',
            'Check that data exists for the period',
            'Verify write permissions on export directory',
            'Try exporting a smaller date range',
        ],
    },
];

const faqs = [
    {
        q: 'How often should sensors be calibrated?',
        a: 'Sensors should be calibrated every 3 months under normal operating conditions, or immediately after any physical disturbance or major environmental change.',
    },
    {
        q: 'Can multiple users access the system simultaneously?',
        a: 'Yes. RDPMS supports concurrent multi-user access with role-based permissions. Admins, Operators, and Viewers each have distinct access levels.',
    },
    {
        q: 'What happens if the internet connection drops?',
        a: 'The system continues logging data locally. Once connectivity is restored, data syncs automatically to the cloud. No manual intervention is required for normal outages under 24 hours.',
    },
    {
        q: 'How do I add a new IoT device?',
        a: 'Navigate to Equipment Master > Add Equipment, fill in the device details, assign it to a station, and configure its IoT channel. The device should appear on the dashboard within 2 minutes.',
    },
    {
        q: 'What file formats are supported for data export?',
        a: 'RDPMS supports export in Excel (.xlsx), CSV, and PDF report formats. JSON export is available via the API for integration purposes.',
    },
    {
        q: 'How do I restore a previous configuration?',
        a: 'Go to Settings > Configuration Backup. Select a restore point from the list and click Restore. A system restart will be required to apply the configuration.',
    },
    {
        q: 'Is the system compatible with third-party sensors?',
        a: 'RDPMS supports Modbus RTU/TCP, MQTT, and OPC-UA protocols. Any sensor conforming to these standards can be integrated. Contact support for custom driver development.',
    },
];

const defaultInstallDocs = [
    {
        id: 'hw',
        title: 'Hardware Installation Guide',
        size: '4.2 MB',
        pages: 64,
        icon: 'mdi mdi-cog',
        color: '#F9B74C',
    },
    {
        id: 'sw',
        title: 'Software Deployment Manual',
        size: '2.8 MB',
        pages: 48,
        icon: 'mdi mdi-disc',
        color: '#0d6efd',
    },
    {
        id: 'cc',
        title: 'Commissioning Checklist',
        size: '0.9 MB',
        pages: 12,
        icon: 'mdi mdi-clipboard-check',
        color: '#198754',
    },
    {
        id: 'wd',
        title: 'Wiring & Schematic Diagrams',
        size: '6.1 MB',
        pages: 36,
        icon: 'mdi mdi-ruler-square-compass',
        color: '#6f42c1',
    },
];

const defaultChapters = [
    { id: 1, title: 'System Overview & Architecture' },
    { id: 2, title: 'Dashboard Navigation' },
    { id: 3, title: 'Station & Equipment Management' },
    { id: 4, title: 'Data Monitoring & Logging' },
    { id: 5, title: 'Reports & Analytics' },
    { id: 6, title: 'User & Role Management' },
    { id: 7, title: 'Network & IoT Configuration' },
    { id: 8, title: 'Maintenance & Upkeep' },
];

const defaultWorkflows = [
    'Live Monitoring',
    'Alarm Configuration',
    'Data Export',
    'Predictive Analytics',
    'User Onboarding',
    'Device Registration',
    'Report Scheduling',
    'Audit Trails',
    'Backup & Restore',
    'API Integration',
];

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const HelpDocumentation = () => {
    const currentYear = new Date().getFullYear();

    /* ── General UI ── */
    const [activeSection, setActiveSection] = useState('manual');
    const [openFaq, setOpenFaq] = useState(null);
    const [openIssue, setOpenIssue] = useState(null);
    const [playingVideo, setPlayingVideo] = useState(null);
    const [dbReady, setDbReady] = useState(false);

    /* ── Video list ── */
    const [videoList, setVideoList] = useState(defaultVideos);
    const videoInputRefs = useRef({});

    /* ── Manual state ── */
    const [manualMeta, setManualMeta] = useState({ title: 'RDPMS User Manual', version: '3.2', pages: 148 });
    const [manualPdfs, setManualPdfs] = useState([]); // [{ id, name, sizeMB, uploadedAt, url(blob), mimeType }]
    const [activePdfId, setActivePdfId] = useState(null);
    const [viewingPdfId, setViewingPdfId] = useState(null);
    const [chapters, setChapters] = useState(defaultChapters);
    const [workflows, setWorkflows] = useState(defaultWorkflows);
    const [dragOver, setDragOver] = useState(false);
    const [editingChapter, setEditingChapter] = useState(null);
    const [newChapterTitle, setNewChapterTitle] = useState('');
    const [newWorkflow, setNewWorkflow] = useState('');
    const [editingMeta, setEditingMeta] = useState(false);
    const [metaDraft, setMetaDraft] = useState({});
    const pdfInputRef = useRef(null);

    /* ── Install docs ── */
    const [installDocs, setInstallDocs] = useState(defaultInstallDocs.map((d) => ({ ...d, uploadedPdf: null })));
    const [viewingInstallPdf, setViewingInstallPdf] = useState(null);
    const installInputRefs = useRef({});

    // Revoke blob URLs on unmount to avoid memory leaks
    const blobUrlsRef = useRef([]);
    const trackBlob = (url) => {
        blobUrlsRef.current.push(url);
        return url;
    };
    useEffect(() => () => blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u)), []);

    /* ══════════════════════════════════════════
       LOAD ALL DATA FROM INDEXEDDB ON MOUNT
    ══════════════════════════════════════════ */
    useEffect(() => {
        (async () => {
            try {
                // Meta
                const meta = await MetaStore.get('manual_meta', {
                    title: 'RDPMS User Manual',
                    version: '3.2',
                    pages: 148,
                });
                setManualMeta(meta);
                setMetaDraft(meta);

                const chaps = await MetaStore.get('chapters', defaultChapters);
                setChapters(chaps);

                const wfs = await MetaStore.get('workflows', defaultWorkflows);
                setWorkflows(wfs);

                // Manual PDFs
                const pdfRows = await idbGetAll('manual_pdfs');
                // Each row: { key: id, value: { name, sizeMB, uploadedAt, mimeType, buffer } }
                const pdfs = pdfRows.map(({ key, value }) => ({
                    id: key,
                    name: value.name,
                    sizeMB: value.sizeMB,
                    uploadedAt: value.uploadedAt,
                    mimeType: value.mimeType,
                    url: trackBlob(bufferToUrl(value.buffer, value.mimeType)),
                }));
                // Sort newest first (key is timestamp)
                pdfs.sort((a, b) => b.id - a.id);
                setManualPdfs(pdfs);
                if (pdfs.length) setActivePdfId(pdfs[0].id);

                // Install PDFs
                const installRows = await idbGetAll('install_pdfs');
                if (installRows.length) {
                    const map = {};
                    installRows.forEach(({ key, value }) => {
                        map[key] = {
                            name: value.name,
                            sizeMB: value.sizeMB,
                            uploadedAt: value.uploadedAt,
                            mimeType: value.mimeType,
                            url: trackBlob(bufferToUrl(value.buffer, value.mimeType)),
                        };
                    });
                    setInstallDocs((prev) => prev.map((d) => ({ ...d, uploadedPdf: map[d.id] || null })));
                }

                // Videos
                const videoRows = await idbGetAll('videos');
                if (videoRows.length) {
                    const map = {};
                    videoRows.forEach(({ key, value }) => {
                        map[key] = {
                            url: trackBlob(bufferToUrl(value.buffer, value.mimeType)),
                            fileName: value.fileName,
                            mimeType: value.mimeType,
                            // Restore real duration saved at upload time; fall back to default if absent
                            ...(value.duration ? { duration: value.duration } : {}),
                        };
                    });
                    setVideoList((prev) => prev.map((v) => (map[v.id] ? { ...v, ...map[v.id] } : v)));
                }

                setDbReady(true);
            } catch (err) {
                console.error('IDB load error', err);
                setDbReady(true);
            }
        })();
    }, []);

    /* ── Persist meta/chapters/workflows whenever they change (after DB ready) ── */
    useEffect(() => {
        if (dbReady) MetaStore.set('manual_meta', manualMeta);
    }, [manualMeta, dbReady]);
    useEffect(() => {
        if (dbReady) MetaStore.set('chapters', chapters);
    }, [chapters, dbReady]);
    useEffect(() => {
        if (dbReady) MetaStore.set('workflows', workflows);
    }, [workflows, dbReady]);

    /* ══════════════════════════════════════════
       VIDEO HANDLERS
    ══════════════════════════════════════════ */
    const handleVideoUpload = async (videoId, file) => {
        if (!file) return;
        const allowed = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
        if (!allowed.includes(file.type)) {
            toast.error('Please upload a valid video file (MP4, WebM, OGG, MOV).', { position: 'top-right' });
            return;
        }
        try {
            const buffer = await fileToBuffer(file);
            const url = trackBlob(bufferToUrl(buffer, file.type));
            // Read the real duration from the actual video file
            const realDuration = await getVideoDuration(url);
            await idbSet('videos', videoId, {
                buffer,
                fileName: file.name,
                mimeType: file.type,
                duration: realDuration,
            });
            setVideoList((prev) =>
                prev.map((v) =>
                    v.id === videoId
                        ? {
                              ...v,
                              videoUrl: url,
                              fileName: file.name,
                              mimeType: file.type,
                              duration: realDuration ?? v.duration,
                          }
                        : v
                )
            );
            setPlayingVideo((prev) =>
                prev?.id === videoId
                    ? { ...prev, videoUrl: url, fileName: file.name, duration: realDuration ?? prev.duration }
                    : prev
            );
            const title = videoList.find((v) => v.id === videoId)?.title;
            toast.success(`Video uploaded for "${title}".`, { position: 'top-right' });
        } catch {
            toast.error('Failed to read video file.', { position: 'top-right' });
        }
    };

    const removeVideo = async (videoId) => {
        await idbDel('videos', videoId);
        setVideoList((prev) =>
            prev.map((v) => (v.id === videoId ? { ...v, videoUrl: null, fileName: null, mimeType: null } : v))
        );
        if (playingVideo?.id === videoId) setPlayingVideo((prev) => ({ ...prev, videoUrl: null, fileName: null }));
    };

    /* ══════════════════════════════════════════
       MANUAL PDF HANDLERS
    ══════════════════════════════════════════ */
    const handleManualPdfUpload = useCallback(async (file) => {
        if (!file) return;
        if (file.type !== 'application/pdf') {
            toast.error('Only PDF files are accepted.', { position: 'top-right' });
            return;
        }
        try {
            const buffer = await fileToBuffer(file);
            const sizeMB = (file.size / 1024 / 1024).toFixed(1) + ' MB';
            const id = Date.now();
            const uploadedAt = new Date().toLocaleString();
            await idbSet('manual_pdfs', id, {
                buffer,
                name: file.name,
                sizeMB,
                uploadedAt,
                mimeType: 'application/pdf',
            });
            const url = trackBlob(bufferToUrl(buffer, 'application/pdf'));
            const entry = { id, name: file.name, sizeMB, uploadedAt, mimeType: 'application/pdf', url };
            setManualPdfs((prev) => [entry, ...prev]);
            setActivePdfId(id);
            setViewingPdfId(null);
            toast.success(`"${file.name}" uploaded successfully.`, { position: 'top-right' });
        } catch {
            toast.error('Failed to read PDF.', { position: 'top-right' });
        }
    }, []);

    const removeManualPdf = async (id) => {
        await idbDel('manual_pdfs', id);
        setManualPdfs((prev) => prev.filter((p) => p.id !== id));
        if (activePdfId === id) setActivePdfId(null);
        if (viewingPdfId === id) setViewingPdfId(null);
    };

    const onPdfFileInput = (e) => {
        handleManualPdfUpload(e.target.files[0]);
        e.target.value = null;
    };
    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleManualPdfUpload(e.dataTransfer.files[0]);
    };

    /* ══════════════════════════════════════════
       INSTALL PDF HANDLERS
    ══════════════════════════════════════════ */
    const handleInstallPdfUpload = async (docId, file) => {
        if (!file) return;
        if (file.type !== 'application/pdf') {
            toast.error('Only PDF files are accepted.', { position: 'top-right' });
            return;
        }
        try {
            const buffer = await fileToBuffer(file);
            const sizeMB = (file.size / 1024 / 1024).toFixed(1) + ' MB';
            const uploadedAt = new Date().toLocaleString();
            await idbSet('install_pdfs', docId, {
                buffer,
                name: file.name,
                sizeMB,
                uploadedAt,
                mimeType: 'application/pdf',
            });
            const url = trackBlob(bufferToUrl(buffer, 'application/pdf'));
            const entry = { name: file.name, sizeMB, uploadedAt, mimeType: 'application/pdf', url };
            setInstallDocs((prev) => prev.map((d) => (d.id === docId ? { ...d, uploadedPdf: entry } : d)));
            toast.success(`"${file.name}" uploaded.`, { position: 'top-right' });
        } catch {
            toast.error('Failed to read PDF.', { position: 'top-right' });
        }
    };

    /* ── Chapter handlers ── */
    const saveChapterEdit = (idx) => {
        if (!editingChapter?.value?.trim()) return;
        setChapters((c) => c.map((ch, i) => (i === idx ? { ...ch, title: editingChapter.value } : ch)));
        setEditingChapter(null);
    };
    const deleteChapter = (idx) => setChapters((c) => c.filter((_, i) => i !== idx));
    const addChapter = () => {
        if (!newChapterTitle.trim()) return;
        setChapters((c) => [...c, { id: Date.now(), title: newChapterTitle.trim() }]);
        setNewChapterTitle('');
        toast.success('Chapter added.', { position: 'top-right' });
    };

    /* ── Workflow handlers ── */
    const deleteWorkflow = (idx) => setWorkflows((w) => w.filter((_, i) => i !== idx));
    const addWorkflow = () => {
        if (!newWorkflow.trim()) return;
        setWorkflows((w) => [...w, newWorkflow.trim()]);
        setNewWorkflow('');
    };

    /* ── Meta save ── */
    const saveMeta = () => {
        setManualMeta({ ...metaDraft });
        setEditingMeta(false);
        toast.success('Manual info updated.', { position: 'top-right' });
    };

    const activePdf = manualPdfs.find((p) => p.id === activePdfId) || null;
    const viewingPdf = manualPdfs.find((p) => p.id === viewingPdfId) || null;

    /* ── Shared button styles ── */
    const submitBtnStyle = {
        backgroundColor: '#F9B74C',
        color: '#000',
        fontWeight: 'bold',
        fontFamily: "'Poppins', sans-serif",
        fontSize: 13,
        border: 'none',
        borderRadius: 4,
        padding: '7px 22px',
        textTransform: 'uppercase',
        cursor: 'pointer',
    };
    const outlineBtnStyle = {
        backgroundColor: 'transparent',
        color: '#F9B74C',
        fontWeight: 'bold',
        fontFamily: "'Poppins', sans-serif",
        fontSize: 13,
        border: '1px solid #F9B74C',
        borderRadius: 4,
        padding: '7px 22px',
        textTransform: 'uppercase',
        cursor: 'pointer',
    };
    const iconBtnStyle = {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '2px 6px',
        color: '#adb5bd',
        fontSize: 13,
    };
    const navItemStyle = (id) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        cursor: 'pointer',
        fontSize: 13,
        fontFamily: "'Poppins', sans-serif",
        fontWeight: activeSection === id ? 600 : 400,
        color: activeSection === id ? '#000' : '#6c757d',
        background: activeSection === id ? '#FFF8EC' : 'transparent',
        borderLeft: activeSection === id ? '3px solid #F9B74C' : '3px solid transparent',
        transition: 'all 0.15s',
    });

    /* ══════════════════════════════════════════
       RENDER
    ══════════════════════════════════════════ */
    return (
        <>
            <ToastContainer />
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
                body, .help-page * { font-family: 'Poppins', sans-serif !important; }
                .help-page .point-title { font-size: 15px; font-weight: 600; color: #333; }
                .help-page .report-alert-card { background: #fff; border: 1px solid #dee2e6; border-radius: 6px; box-shadow: 0px 0px 35px rgba(154,161,171,0.15); }
                .help-page .section-card { background: #fff; border: 1px solid #dee2e6; border-radius: 6px; box-shadow: 0px 0px 35px rgba(154,161,171,0.15); padding: 20px 24px; }
                .drop-zone { border: 2px dashed #dee2e6; border-radius: 8px; padding: 32px 20px; text-align: center; cursor: pointer; transition: border-color 0.2s, background 0.2s; background: #fafafa; }
                .drop-zone.active { border-color: #F9B74C; background: #FFF8EC; }
                .drop-zone:hover  { border-color: #F9B74C; }
                .pdf-iframe-wrap { border: 1px solid #dee2e6; border-radius: 6px; overflow: hidden; background: #f5f5f5; }
                .chapter-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f0f0f0; font-size: 13px; }
                .chapter-row:last-child { border-bottom: none; }
                .chapter-row .actions { opacity: 0; transition: opacity 0.15s; }
                .chapter-row:hover .actions { opacity: 1; }
                .tag-pill { display: inline-flex; align-items: center; gap: 4px; background: #FFF8EC; border: 1px solid #F9B74C; border-radius: 20px; padding: 3px 10px; font-size: 12px; color: #333; margin: 3px; }
                .tag-pill .tag-del { cursor: pointer; color: #adb5bd; font-size: 14px; line-height: 1; margin-left: 2px; }
                .tag-pill .tag-del:hover { color: #dc3545; }
                .pdf-list-item { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border: 1px solid #dee2e6; border-radius: 6px; margin-bottom: 6px; cursor: pointer; transition: border-color 0.2s, background 0.2s; }
                .pdf-list-item:hover { border-color: #F9B74C; }
                .pdf-list-item.active { border-color: #F9B74C; background: #FFF8EC; }
                .video-row { display: flex; flex-wrap: wrap; margin: -8px; }
                .video-col { padding: 8px; width: 25%; box-sizing: border-box; display: flex; }
                @media (max-width:1200px) { .video-col { width: 33.33%; } }
                @media (max-width:900px)  { .video-col { width: 50%; } }
                @media (max-width:560px)  { .video-col { width: 100%; } }
                .video-card { border:1px solid #dee2e6; border-radius:6px; overflow:hidden; cursor:pointer; transition:box-shadow 0.2s,border-color 0.2s; background:#fff; display:flex; flex-direction:column; width:100%; }
                .video-card:hover { border-color:#F9B74C; box-shadow:0 4px 18px rgba(249,183,76,0.18); }
                .video-card-body { padding: 12px 14px; display: flex; flex-direction: column; flex: 1; }
                .video-card-desc { font-size:12px; color:#6c757d; line-height:1.5; flex: 1; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; min-height: 54px; }
                .video-card-footer { display:flex; align-items:center; justify-content:space-between; margin-top: 10px; padding-top: 8px; border-top: 1px solid #f0f0f0; }
                .video-thumb { height: 130px; background: linear-gradient(135deg,#f8f9fa,#e9ecef); display:flex; align-items:center; justify-content:center; position:relative; border-radius:6px 6px 0 0; overflow:hidden; flex-shrink: 0; }
                .video-thumb .play-btn { position:absolute; width:44px; height:44px; border-radius:50%; background:rgba(249,183,76,0.92); display:flex; align-items:center; justify-content:center; font-size:18px; padding-left:3px; color:#000; }
                .video-thumb.has-video { background: #000; }
                .upload-overlay { position:absolute; bottom:0; left:0; right:0; background:rgba(0,0,0,0.55); padding:4px 8px; display:flex; align-items:center; justify-content:center; gap:6px; }
                .video-modal-player { width:100%; border-radius:6px; background:#000; max-height:340px; display:block; }
                .video-upload-zone { border:2px dashed #dee2e6; border-radius:6px; padding:24px; text-align:center; cursor:pointer; transition:border-color 0.2s, background 0.2s; }
                .video-upload-zone:hover { border-color:#F9B74C; background:#FFF8EC; }
                .faq-item { border:1px solid #dee2e6; border-radius:6px; overflow:hidden; margin-bottom:8px; background:#fff; }
                .faq-q { padding:13px 18px; cursor:pointer; font-weight:500; display:flex; justify-content:space-between; align-items:center; gap:10px; font-size:13px; color:#333; }
                .faq-q:hover { background:#FFF8EC; }
                .faq-a { padding:0 18px 14px; font-size:13px; color:#6c757d; line-height:1.7; border-top:1px solid #dee2e6; padding-top:12px; }
                .issue-card { border:1px solid #dee2e6; border-radius:6px; overflow:hidden; margin-bottom:8px; background:#fff; }
                .issue-header { padding:13px 18px; cursor:pointer; display:flex; justify-content:space-between; align-items:center; gap:12px; }
                .issue-header:hover { background:#FFF8EC; }
                .step-bubble { min-width:28px; height:28px; border-radius:50%; background:#F9B74C; color:#000; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
                .doc-card { border:1px solid #dee2e6; border-radius:6px; padding:16px; background:#fff; display:flex; gap:14px; align-items:flex-start; transition:box-shadow 0.2s; }
                .doc-card:hover { box-shadow:0 4px 14px rgba(249,183,76,0.15); border-color:#F9B74C; }
                .req-row { padding:9px 0; border-bottom:1px solid #f0f0f0; font-size:13px; }
                .req-row:last-child { border-bottom:none; }
                .modal-backdrop-custom { position:fixed; inset:0; background:rgba(0,0,0,0.45); display:flex; align-items:center; justify-content:center; z-index:1055; padding:20px; }
                .modal-box { background:#fff; border-radius:8px; padding:28px; max-width:520px; width:100%; position:relative; box-shadow:0 8px 40px rgba(0,0,0,0.18); }
                .nav-section-label { font-size:10px; text-transform:uppercase; letter-spacing:1px; color:#adb5bd; padding:14px 16px 6px; font-weight:600; }
                .input-sm-custom { font-size:12px; border:1px solid #dee2e6; border-radius:4px; padding:5px 10px; outline:none; font-family:'Poppins',sans-serif; }
                .input-sm-custom:focus { border-color:#F9B74C; }
                .badge-uploaded { background:#e8f5e9; color:#198754; border:1px solid #c8e6c9; border-radius:20px; padding:2px 10px; font-size:11px; font-weight:600; }
                .badge-no-pdf   { background:#fff3cd; color:#856404; border:1px solid #ffe69c; border-radius:20px; padding:2px 10px; font-size:11px; font-weight:600; }
                .install-upload-zone { border:2px dashed #dee2e6; border-radius:6px; padding:10px 14px; text-align:center; cursor:pointer; transition:border-color 0.2s, background 0.2s; font-size:11px; color:#6c757d; }
                .install-upload-zone:hover { border-color:#F9B74C; background:#FFF8EC; }
                .idb-loading { display:flex; align-items:center; justify-content:center; min-height:100vh; background:#f5f6fa; font-family:'Poppins',sans-serif; font-size:14px; color:#6c757d; gap:10px; }
            `}</style>

            {!dbReady ? (
                <div className="idb-loading">
                    <div className="spinner-border spinner-border-sm" style={{ color: '#F9B74C' }} />
                    Loading stored data…
                </div>
            ) : (
                <div className="help-page" style={{ display: 'flex', minHeight: '100vh', background: '#f5f6fa' }}>
                    {/* ════════════════ SIDEBAR ════════════════ */}
                    <aside
                        style={{
                            width: 260,
                            minHeight: '100vh',
                            background: '#fff',
                            borderRight: '1px solid #dee2e6',
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                        <div style={{ padding: '18px 16px 10px', borderBottom: '1px solid #dee2e6' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div
                                    style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: 6,
                                        background: '#F9B74C',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <i className="bi bi-book-half" style={{ fontSize: 18, color: '#000' }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2, color: '#212529' }}>
                                        RDPMS
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 10,
                                            color: '#adb5bd',
                                            letterSpacing: '0.6px',
                                            textTransform: 'uppercase',
                                        }}>
                                        Help Center
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 12 }}>
                            <div className="nav-section-label">Documentation</div>
                            {sections.map((s) => (
                                <div key={s.id} style={navItemStyle(s.id)} onClick={() => setActiveSection(s.id)}>
                                    <i
                                        className={`bi ${s.icon}`}
                                        style={{ fontSize: 15, color: activeSection === s.id ? '#F9B74C' : '#adb5bd' }}
                                    />
                                    {s.label}
                                </div>
                            ))}
                        </div>
                        <div style={{ padding: '14px 16px', borderTop: '1px solid #dee2e6' }}>
                            <div style={{ fontSize: 11, color: '#adb5bd', marginBottom: 6 }}>Need more help?</div>
                            <a
                                href="mailto:support@rdpms.com"
                                style={{ fontSize: 12, color: '#F9B74C', textDecoration: 'none', fontWeight: 600 }}>
                                <i className="mdi mdi-email me-1" /> support@rdpms.com
                            </a>
                        </div>
                    </aside>

                    {/* ════════════════ MAIN CONTENT ════════════════ */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 40px' }}>
                        {/* ══════════════════════════════════════ USER MANUAL ══════════════════════════════════════ */}
                        {activeSection === 'manual' && (
                            <div className="container-fluid px-3 pt-3">
                                <div className="row align-items-center gx-2 gy-2 mb-3">
                                    <div className="col-12 col-md-auto">
                                        <h6 className="point-title fw-bold mb-0">User Manual</h6>
                                    </div>
                                    <div className="col-12 col-md-auto ms-md-auto d-flex gap-2">
                                        <button
                                            style={{ ...outlineBtnStyle, padding: '5px 14px', fontSize: 12 }}
                                            onClick={() => {
                                                setEditingMeta(true);
                                                setMetaDraft({ ...manualMeta });
                                            }}>
                                            <i className="mdi mdi-pencil me-1" /> Edit Info
                                        </button>
                                        <input
                                            ref={pdfInputRef}
                                            type="file"
                                            accept=".pdf"
                                            style={{ display: 'none' }}
                                            onChange={onPdfFileInput}
                                        />
                                        <button
                                            style={{ ...submitBtnStyle, padding: '5px 14px', fontSize: 12 }}
                                            onClick={() => pdfInputRef.current?.click()}>
                                            <i className="mdi mdi-upload me-1" /> Upload PDF
                                        </button>
                                    </div>
                                </div>

                                {editingMeta && (
                                    <div
                                        className="report-alert-card p-3 mb-3"
                                        style={{ borderColor: '#F9B74C', background: '#FFF8EC' }}>
                                        <div className="row g-2 align-items-end">
                                            <div className="col-md-5">
                                                <label
                                                    style={{
                                                        fontSize: 11,
                                                        color: '#6c757d',
                                                        marginBottom: 3,
                                                        display: 'block',
                                                    }}>
                                                    Manual Title
                                                </label>
                                                <input
                                                    className="form-control form-control-sm"
                                                    value={metaDraft.title}
                                                    onChange={(e) =>
                                                        setMetaDraft((m) => ({ ...m, title: e.target.value }))
                                                    }
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label
                                                    style={{
                                                        fontSize: 11,
                                                        color: '#6c757d',
                                                        marginBottom: 3,
                                                        display: 'block',
                                                    }}>
                                                    Version
                                                </label>
                                                <input
                                                    className="form-control form-control-sm"
                                                    value={metaDraft.version}
                                                    onChange={(e) =>
                                                        setMetaDraft((m) => ({ ...m, version: e.target.value }))
                                                    }
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label
                                                    style={{
                                                        fontSize: 11,
                                                        color: '#6c757d',
                                                        marginBottom: 3,
                                                        display: 'block',
                                                    }}>
                                                    Pages
                                                </label>
                                                <input
                                                    className="form-control form-control-sm"
                                                    type="number"
                                                    value={metaDraft.pages}
                                                    onChange={(e) =>
                                                        setMetaDraft((m) => ({ ...m, pages: e.target.value }))
                                                    }
                                                />
                                            </div>
                                            <div className="col-md-3 d-flex gap-2">
                                                <button
                                                    style={{ ...submitBtnStyle, padding: '5px 14px', fontSize: 12 }}
                                                    onClick={saveMeta}>
                                                    <i className="fas fa-save me-1" />
                                                    Save
                                                </button>
                                                <button
                                                    style={{ ...outlineBtnStyle, padding: '5px 14px', fontSize: 12 }}
                                                    onClick={() => setEditingMeta(false)}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {manualPdfs.length === 0 && (
                                    <div
                                        className={`drop-zone mb-3${dragOver ? ' active' : ''}`}
                                        onClick={() => pdfInputRef.current?.click()}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            setDragOver(true);
                                        }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={onDrop}>
                                        <i
                                            className="mdi mdi-upload me-1"
                                            style={{
                                                fontSize: 36,
                                                color: '#F9B74C',
                                                marginBottom: 10,
                                                display: 'block',
                                            }}
                                        />
                                        <div style={{ fontWeight: 600, fontSize: 14, color: '#333' }}>
                                            Drop your PDF here
                                        </div>
                                        <div style={{ fontSize: 12, color: '#6c757d', marginTop: 4 }}>
                                            or click to browse — PDF files only
                                        </div>
                                    </div>
                                )}

                                {viewingPdf && (
                                    <div className="pdf-iframe-wrap mb-3">
                                        <div
                                            style={{
                                                background: '#f0f0f0',
                                                padding: '8px 14px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                borderBottom: '1px solid #dee2e6',
                                            }}>
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                                                <i
                                                    className="mdi mdi-file-pdf-box"
                                                    style={{ fontSize: 18, color: '#dc3545' }}
                                                />
                                                {viewingPdf.name}
                                            </span>
                                            <div className="d-flex gap-2 align-items-center">
                                                <span style={{ fontSize: 11, color: '#6c757d' }}>
                                                    {viewingPdf.sizeMB}
                                                </span>
                                                <button
                                                    style={{ ...outlineBtnStyle, padding: '3px 10px', fontSize: 11 }}
                                                    onClick={() => setViewingPdfId(null)}>
                                                    <i className="mdi mdi-close" />
                                                    Close
                                                </button>
                                            </div>
                                        </div>
                                        <iframe
                                            src={viewingPdf.url}
                                            title="PDF Viewer"
                                            style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
                                        />
                                    </div>
                                )}

                                <div className="row g-3">
                                    <div className="col-md-5">
                                        <div className="report-alert-card p-3 h-100">
                                            <div
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: 13,
                                                    marginBottom: 14,
                                                    color: '#333',
                                                    borderBottom: '1px solid #f0f0f0',
                                                    paddingBottom: 8,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}>
                                                <span>
                                                    <i
                                                        className="mdi mdi-file-pdf-box"
                                                        style={{ fontSize: 18, color: '#dc3545' }}
                                                    />
                                                    Manual Documents
                                                </span>
                                                <span style={{ fontSize: 11, color: '#adb5bd' }}>
                                                    {manualPdfs.length} file{manualPdfs.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>

                                            {activePdf && (
                                                <div
                                                    style={{
                                                        background: '#f8f9fa',
                                                        borderRadius: 6,
                                                        padding: '10px 14px',
                                                        marginBottom: 12,
                                                    }}>
                                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#333' }}>
                                                        {manualMeta.title}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                                                        Version {manualMeta.version} · {manualMeta.pages} pages · PDF
                                                    </div>
                                                </div>
                                            )}

                                            {manualPdfs.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                                                    <i
                                                        className="mdi mdi-upload me-1"
                                                        style={{
                                                            fontSize: 40,
                                                            color: '#dee2e6',
                                                            marginBottom: 10,
                                                            display: 'block',
                                                        }}
                                                    />
                                                    <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 10 }}>
                                                        No PDFs uploaded yet
                                                    </div>
                                                    <span className="badge-no-pdf">Awaiting Upload</span>
                                                    <div className="mt-3">
                                                        <button
                                                            style={{
                                                                ...submitBtnStyle,
                                                                fontSize: 12,
                                                                padding: '6px 16px',
                                                            }}
                                                            onClick={() => pdfInputRef.current?.click()}>
                                                            <i className="mdi mdi-upload me-1" />
                                                            Upload PDF
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                                                    {manualPdfs.map((pdf) => (
                                                        <div
                                                            key={pdf.id}
                                                            className={`pdf-list-item${
                                                                activePdfId === pdf.id ? ' active' : ''
                                                            }`}
                                                            onClick={() => setActivePdfId(pdf.id)}>
                                                            <div
                                                                style={{
                                                                    width: 36,
                                                                    height: 44,
                                                                    background: '#fff3cd',
                                                                    border: '1px solid #ffe69c',
                                                                    borderRadius: 4,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    flexShrink: 0,
                                                                }}>
                                                                <i
                                                                    className="mdi mdi-file-pdf-box"
                                                                    style={{ fontSize: 18, color: '#dc3545' }}
                                                                />
                                                            </div>
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize: 12,
                                                                        color: '#333',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }}>
                                                                    {pdf.name}
                                                                </div>
                                                                <div style={{ fontSize: 10, color: '#adb5bd' }}>
                                                                    {pdf.sizeMB} · {pdf.uploadedAt}
                                                                </div>
                                                            </div>
                                                            {activePdfId === pdf.id && (
                                                                <span className="badge-uploaded">
                                                                    <i className="mdi mdi-check-circle me-1" />
                                                                    Active
                                                                </span>
                                                            )}
                                                            <div
                                                                className="d-flex gap-1"
                                                                onClick={(e) => e.stopPropagation()}>
                                                                <button
                                                                    style={iconBtnStyle}
                                                                    title="View"
                                                                    onClick={() =>
                                                                        setViewingPdfId(
                                                                            viewingPdfId === pdf.id ? null : pdf.id
                                                                        )
                                                                    }>
                                                                    <i
                                                                        className="mdi mdi-eye"
                                                                        style={{ color: '#F9B74C' }}
                                                                    />
                                                                </button>
                                                                <a
                                                                    href={pdf.url}
                                                                    download={pdf.name}
                                                                    style={{
                                                                        color: '#adb5bd',
                                                                        fontSize: 13,
                                                                        padding: '2px 6px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                    }}
                                                                    title="Download">
                                                                    <i
                                                                        className="mdi mdi-download"
                                                                        style={{ color: '#1ad32a' }}
                                                                    />
                                                                </a>
                                                                <button
                                                                    style={iconBtnStyle}
                                                                    title="Delete"
                                                                    onClick={() => removeManualPdf(pdf.id)}>
                                                                    <i
                                                                        className="mdi mdi-delete"
                                                                        style={{ color: '#dc3545' }}
                                                                    />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {manualPdfs.length > 0 && (
                                                <div
                                                    style={{
                                                        marginTop: 10,
                                                        borderTop: '1px solid #f0f0f0',
                                                        paddingTop: 10,
                                                    }}>
                                                    <button
                                                        style={{
                                                            ...outlineBtnStyle,
                                                            padding: '5px 14px',
                                                            fontSize: 12,
                                                            width: '100%',
                                                        }}
                                                        onClick={() => pdfInputRef.current?.click()}>
                                                        <i className="fas fa-plus me-1" />
                                                        Add Another PDF
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-md-7">
                                        <div className="section-card h-100">
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    marginBottom: 14,
                                                }}>
                                                <div style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>
                                                    <i
                                                        className="mdi mdi-format-list-numbered me-1"
                                                        style={{ color: '#F9B74C' }}
                                                    />
                                                    Table of Contents
                                                </div>
                                                <span style={{ fontSize: 11, color: '#adb5bd' }}>
                                                    {chapters.length} chapters
                                                </span>
                                            </div>
                                            <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 12 }}>
                                                {chapters.map((ch, idx) => (
                                                    <div className="chapter-row" key={ch.id}>
                                                        {editingChapter?.index === idx ? (
                                                            <div className="d-flex gap-2 flex-1 align-items-center">
                                                                <input
                                                                    className="input-sm-custom flex-1"
                                                                    value={editingChapter.value}
                                                                    onChange={(e) =>
                                                                        setEditingChapter((ec) => ({
                                                                            ...ec,
                                                                            value: e.target.value,
                                                                        }))
                                                                    }
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') saveChapterEdit(idx);
                                                                        if (e.key === 'Escape') setEditingChapter(null);
                                                                    }}
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    style={{
                                                                        ...submitBtnStyle,
                                                                        padding: '3px 10px',
                                                                        fontSize: 11,
                                                                    }}
                                                                    onClick={() => saveChapterEdit(idx)}>
                                                                    Save
                                                                </button>
                                                                <button
                                                                    style={{
                                                                        ...outlineBtnStyle,
                                                                        padding: '3px 10px',
                                                                        fontSize: 11,
                                                                    }}
                                                                    onClick={() => setEditingChapter(null)}>
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <span
                                                                    style={{
                                                                        color: '#F9B74C',
                                                                        fontWeight: 600,
                                                                        fontSize: 12,
                                                                        minWidth: 72,
                                                                        flexShrink: 0,
                                                                    }}>
                                                                    Chapter {idx + 1}
                                                                </span>
                                                                <span
                                                                    style={{ color: '#495057', fontSize: 12, flex: 1 }}>
                                                                    {ch.title}
                                                                </span>
                                                                <div className="actions d-flex gap-1">
                                                                    <button
                                                                        style={iconBtnStyle}
                                                                        title="Edit"
                                                                        onClick={() =>
                                                                            setEditingChapter({
                                                                                index: idx,
                                                                                value: ch.title,
                                                                            })
                                                                        }>
                                                                        <i
                                                                            className="mdi mdi-pencil"
                                                                            style={{ color: '#F9B74C' }}
                                                                        />
                                                                    </button>
                                                                    <button
                                                                        style={iconBtnStyle}
                                                                        title="Delete"
                                                                        onClick={() => deleteChapter(idx)}>
                                                                        <i
                                                                            className="mdi mdi-delete"
                                                                            style={{ color: '#dc3545' }}
                                                                        />
                                                                    </button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                            <div
                                                className="d-flex gap-2 align-items-center"
                                                style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                                                <input
                                                    className="input-sm-custom flex-1"
                                                    placeholder="Add new chapter title…"
                                                    value={newChapterTitle}
                                                    onChange={(e) => setNewChapterTitle(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') addChapter();
                                                    }}
                                                />
                                                <button
                                                    style={{ ...submitBtnStyle, padding: '5px 14px', fontSize: 12 }}
                                                    onClick={addChapter}>
                                                    <i className="fas fa-plus me-1" />
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="section-card mt-3">
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            marginBottom: 12,
                                        }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>
                                            <i className="mdi mdi-source-branch me-1" style={{ color: '#F9B74C' }} />
                                            Covered Workflows
                                        </div>
                                        <span style={{ fontSize: 11, color: '#adb5bd' }}>
                                            {workflows.length} workflows
                                        </span>
                                    </div>
                                    <div style={{ marginBottom: 12 }}>
                                        {workflows.map((wf, idx) => (
                                            <span key={idx} className="tag-pill">
                                                {wf}
                                                <span
                                                    className="tag-del"
                                                    onClick={() => deleteWorkflow(idx)}
                                                    title="Remove">
                                                    ×
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                    <div
                                        className="d-flex gap-2 align-items-center"
                                        style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                                        <input
                                            className="input-sm-custom"
                                            style={{ width: 220 }}
                                            placeholder="Add workflow tag…"
                                            value={newWorkflow}
                                            onChange={(e) => setNewWorkflow(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') addWorkflow();
                                            }}
                                        />
                                        <button
                                            style={{ ...submitBtnStyle, padding: '5px 14px', fontSize: 12 }}
                                            onClick={addWorkflow}>
                                            <i className="fas fa-plus me-1" />
                                            Add
                                        </button>
                                    </div>
                                </div>

                                <div style={{ textAlign: 'center', color: '#adb5bd', fontSize: 12, marginTop: 28 }}>
                                    Copyright {currentYear} Hummingbird TekSystems, Inc. | CONFIDENTIAL
                                </div>
                            </div>
                        )}

                        {/* ══════════════════════════════════════ TRAINING VIDEOS ══════════════════════════════════════ */}
                        {activeSection === 'videos' && (
                            <div className="container-fluid px-3 pt-3">
                                <div className="row align-items-center gx-2 gy-2 mb-3">
                                    <div className="col-12 col-md-auto">
                                        <h6 className="point-title fw-bold mb-0">Training Videos</h6>
                                    </div>
                                    <div className="col-12 col-md-auto ms-md-auto d-flex align-items-center gap-3">
                                        <span style={{ fontSize: 12, color: '#6c757d' }}>
                                            <i className="mdi mdi-check-circle me-1" style={{ color: '#198754' }} />
                                            {videoList.filter((v) => v.videoUrl).length} / {videoList.length} uploaded
                                        </span>
                                    </div>
                                </div>

                                <div
                                    className="report-alert-card p-3 mb-3"
                                    style={{ background: '#FFF8EC', borderColor: '#F9B74C' }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <i
                                            className="mdi mdi-information-outline"
                                            style={{ color: '#F9B74C', fontSize: 16 }}
                                        />
                                        <span style={{ fontSize: 12, color: '#333' }}>
                                            Click <strong>Upload</strong> on any card to attach a video. Videos are
                                            saved in IndexedDB and persist after page refresh.
                                        </span>
                                    </div>
                                </div>

                                <div className="video-row">
                                    {videoList.map((v) => (
                                        <div key={v.id} className="video-col">
                                            <input
                                                type="file"
                                                accept="video/mp4,video/webm,video/ogg,video/quicktime"
                                                style={{ display: 'none' }}
                                                ref={(el) => {
                                                    videoInputRefs.current[v.id] = el;
                                                }}
                                                onChange={(e) => {
                                                    handleVideoUpload(v.id, e.target.files[0]);
                                                    e.target.value = null;
                                                }}
                                            />
                                            <div className="video-card" onClick={() => setPlayingVideo({ ...v })}>
                                                <div className={`video-thumb${v.videoUrl ? ' has-video' : ''}`}>
                                                    {v.videoUrl ? (
                                                        <>
                                                            <video
                                                                src={v.videoUrl}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                    display: 'block',
                                                                }}
                                                                muted
                                                                preload="metadata"
                                                            />
                                                            <div className="play-btn">▶</div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i
                                                                className="fas fa-play-circle"
                                                                style={{ fontSize: 42, color: '#dee2e6' }}
                                                            />
                                                            <div className="play-btn">▶</div>
                                                        </>
                                                    )}
                                                    <div
                                                        className="upload-overlay"
                                                        onClick={(e) => e.stopPropagation()}>
                                                        {v.videoUrl ? (
                                                            <>
                                                                <button
                                                                    style={{
                                                                        background: 'rgba(249,183,76,0.9)',
                                                                        border: 'none',
                                                                        borderRadius: 4,
                                                                        padding: '2px 8px',
                                                                        fontSize: 10,
                                                                        fontWeight: 700,
                                                                        cursor: 'pointer',
                                                                        color: '#000',
                                                                        fontFamily: "'Poppins',sans-serif",
                                                                    }}
                                                                    onClick={() =>
                                                                        videoInputRefs.current[v.id]?.click()
                                                                    }>
                                                                    <i className="mdi mdi-sync me-1" />
                                                                    Replace
                                                                </button>
                                                                <button
                                                                    style={{
                                                                        background: 'rgba(220,53,69,0.85)',
                                                                        border: 'none',
                                                                        borderRadius: 4,
                                                                        padding: '2px 8px',
                                                                        fontSize: 10,
                                                                        fontWeight: 700,
                                                                        cursor: 'pointer',
                                                                        color: '#fff',
                                                                        fontFamily: "'Poppins',sans-serif",
                                                                    }}
                                                                    onClick={() => removeVideo(v.id)}>
                                                                    <i className="mdi mdi-delete me-1" />
                                                                    Remove
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                style={{
                                                                    background: 'rgba(249,183,76,0.92)',
                                                                    border: 'none',
                                                                    borderRadius: 4,
                                                                    padding: '3px 12px',
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    cursor: 'pointer',
                                                                    color: '#000',
                                                                    fontFamily: "'Poppins',sans-serif",
                                                                }}
                                                                onClick={() => videoInputRefs.current[v.id]?.click()}>
                                                                <i className="mdi mdi-upload me-1" />
                                                                Upload Video
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="video-card-body">
                                                    <div
                                                        style={{
                                                            fontWeight: 600,
                                                            fontSize: 13,
                                                            marginBottom: 6,
                                                            color: '#333',
                                                        }}>
                                                        {v.title}
                                                    </div>
                                                    <div className="video-card-desc">{v.desc}</div>
                                                    <div className="video-card-footer">
                                                        <span
                                                            style={{
                                                                fontSize: 11,
                                                                background: '#FFF8EC',
                                                                border: '1px solid #F9B74C',
                                                                borderRadius: 20,
                                                                padding: '2px 10px',
                                                                color: '#333',
                                                            }}>
                                                            <i className="mdi mdi-clock-outline me-1" />
                                                            {v.duration}
                                                        </span>
                                                        <span
                                                            style={{
                                                                fontSize: 12,
                                                                color: v.videoUrl ? '#F9B74C' : '#adb5bd',
                                                                fontWeight: 600,
                                                            }}>
                                                            {v.videoUrl ? 'Watch →' : 'No video yet'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ textAlign: 'center', color: '#adb5bd', fontSize: 12, marginTop: 28 }}>
                                    Copyright {currentYear} Hummingbird TekSystems, Inc. | CONFIDENTIAL
                                </div>
                            </div>
                        )}

                        {/* ══════════════════════════════════════ INSTALLATION ══════════════════════════════════════ */}
                        {activeSection === 'installation' && (
                            <div className="container-fluid px-3 pt-3">
                                <div className="row align-items-center gx-2 gy-2 mb-3">
                                    <div className="col-12 col-md-auto">
                                        <h6 className="point-title fw-bold mb-0">
                                            Installation & Commissioning Manual
                                        </h6>
                                    </div>
                                </div>

                                {viewingInstallPdf &&
                                    (() => {
                                        const doc = installDocs.find((d) => d.id === viewingInstallPdf);
                                        const pdf = doc?.uploadedPdf;
                                        if (!pdf) return null;
                                        return (
                                            <div className="pdf-iframe-wrap mb-3">
                                                <div
                                                    style={{
                                                        background: '#f0f0f0',
                                                        padding: '8px 14px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        borderBottom: '1px solid #dee2e6',
                                                    }}>
                                                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                                                        <i
                                                            className="mdi mdi-file-pdf-box"
                                                            style={{ fontSize: 18, color: '#dc3545' }}
                                                        />
                                                        {pdf.name}
                                                    </span>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <span style={{ fontSize: 11, color: '#6c757d' }}>
                                                            {pdf.sizeMB}
                                                        </span>
                                                        <button
                                                            style={{
                                                                ...outlineBtnStyle,
                                                                padding: '3px 10px',
                                                                fontSize: 11,
                                                            }}
                                                            onClick={() => setViewingInstallPdf(null)}>
                                                            <i className="mdi mdi-close" />
                                                            Close
                                                        </button>
                                                    </div>
                                                </div>
                                                <iframe
                                                    src={pdf.url}
                                                    title="PDF Viewer"
                                                    style={{
                                                        width: '100%',
                                                        height: '70vh',
                                                        border: 'none',
                                                        display: 'block',
                                                    }}
                                                />
                                            </div>
                                        );
                                    })()}

                                <div className="row g-3 mb-3">
                                    {installDocs.map((doc) => (
                                        <div key={doc.id} className="col-md-6">
                                            <input
                                                type="file"
                                                accept=".pdf"
                                                style={{ display: 'none' }}
                                                ref={(el) => {
                                                    installInputRefs.current[doc.id] = el;
                                                }}
                                                onChange={(e) => {
                                                    handleInstallPdfUpload(doc.id, e.target.files[0]);
                                                    e.target.value = null;
                                                }}
                                            />
                                            <div
                                                className="doc-card"
                                                style={{ flexDirection: 'column', gap: 0, alignItems: 'stretch' }}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        gap: 14,
                                                        alignItems: 'flex-start',
                                                        marginBottom: 12,
                                                    }}>
                                                    <div
                                                        style={{
                                                            width: 48,
                                                            height: 48,
                                                            borderRadius: 8,
                                                            flexShrink: 0,
                                                            background: `${doc.color}22`,
                                                            border: `1px solid ${doc.color}55`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: 22,
                                                            color: doc.color,
                                                        }}>
                                                        <i className={doc.icon} />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
                                                            {doc.title}
                                                        </div>
                                                        {doc.uploadedPdf ? (
                                                            <div style={{ fontSize: 11, color: '#6c757d' }}>
                                                                <span className="badge-uploaded me-1">
                                                                    <i
                                                                        className="mdi mdi-check-circle me-1"
                                                                        style={{ color: '#198754' }}
                                                                    />
                                                                    Uploaded
                                                                </span>
                                                                {doc.uploadedPdf.name} · {doc.uploadedPdf.sizeMB}
                                                            </div>
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    fontSize: 11,
                                                                    color: '#6c757d',
                                                                    marginBottom: 0,
                                                                }}>
                                                                PDF · {doc.pages} pages · {doc.size}
                                                                <span className="badge-no-pdf ms-2">No file</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {doc.uploadedPdf ? (
                                                        <>
                                                            <button
                                                                style={{
                                                                    ...submitBtnStyle,
                                                                    padding: '5px 14px',
                                                                    fontSize: 11,
                                                                }}
                                                                onClick={() =>
                                                                    setViewingInstallPdf(
                                                                        viewingInstallPdf === doc.id ? null : doc.id
                                                                    )
                                                                }>
                                                                <i
                                                                    className={`mdi ${
                                                                        viewingInstallPdf === doc.id
                                                                            ? 'mdi-close'
                                                                            : 'mdi-eye'
                                                                    } me-1`}
                                                                />
                                                                {viewingInstallPdf === doc.id ? 'Close' : 'Preview'}
                                                            </button>
                                                            <a
                                                                href={doc.uploadedPdf.url}
                                                                download={doc.uploadedPdf.name}
                                                                style={{ textDecoration: 'none' }}>
                                                                <button
                                                                    style={{
                                                                        ...outlineBtnStyle,
                                                                        padding: '5px 14px',
                                                                        fontSize: 11,
                                                                    }}>
                                                                    <i className="mdi mdi-download" />
                                                                    Download
                                                                </button>
                                                            </a>
                                                            <button
                                                                style={{
                                                                    ...outlineBtnStyle,
                                                                    padding: '5px 14px',
                                                                    fontSize: 11,
                                                                    borderColor: '#adb5bd',
                                                                    color: '#6c757d',
                                                                }}
                                                                onClick={() =>
                                                                    installInputRefs.current[doc.id]?.click()
                                                                }>
                                                                <i className="mdi mdi-sync me-1" />
                                                                Replace
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                style={{
                                                                    ...submitBtnStyle,
                                                                    padding: '5px 14px',
                                                                    fontSize: 11,
                                                                }}
                                                                onClick={() =>
                                                                    installInputRefs.current[doc.id]?.click()
                                                                }>
                                                                <i className="mdi mdi-upload me-1" />
                                                                Upload PDF
                                                            </button>
                                                            <button
                                                                style={{
                                                                    ...outlineBtnStyle,
                                                                    padding: '5px 14px',
                                                                    fontSize: 11,
                                                                }}>
                                                                <i className="mdi mdi-download" />
                                                                Download
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="section-card">
                                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>
                                        System Requirements
                                    </div>
                                    <div className="row">
                                        {[
                                            {
                                                label: 'Operating System',
                                                value: 'Windows Server 2019+ / Ubuntu 22.04+',
                                            },
                                            { label: 'RAM', value: 'Minimum 8 GB (16 GB recommended)' },
                                            { label: 'Storage', value: '100 GB SSD (data grows ~2 GB/month)' },
                                            { label: 'Browser', value: 'Chrome 100+, Edge 100+, Firefox 100+' },
                                            { label: 'Network', value: '100 Mbps LAN, stable internet for cloud sync' },
                                            { label: 'Node.js', value: 'v18.x LTS or v20.x LTS' },
                                        ].map(({ label, value }) => (
                                            <div className="col-md-6" key={label}>
                                                <div className="req-row">
                                                    <div style={{ fontSize: 11, color: '#adb5bd', marginBottom: 2 }}>
                                                        {label}
                                                    </div>
                                                    <div style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>
                                                        {value}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', color: '#adb5bd', fontSize: 12, marginTop: 28 }}>
                                    Copyright {currentYear} Hummingbird TekSystems, Inc. | CONFIDENTIAL
                                </div>
                            </div>
                        )}

                        {/* ══════════════════════════════════════ TROUBLESHOOTING ══════════════════════════════════════ */}
                        {activeSection === 'troubleshooting' && (
                            <div className="container-fluid px-3 pt-3">
                                <div className="row align-items-center gx-2 gy-2 mb-3">
                                    <div className="col-12 col-md-auto">
                                        <h6 className="point-title fw-bold mb-0">Troubleshooting Guide</h6>
                                    </div>
                                    <div className="col-12 col-md-auto ms-md-auto">
                                        <span style={{ fontSize: 12, color: '#6c757d' }}>
                                            Click any issue to expand steps
                                        </span>
                                    </div>
                                </div>
                                {issues.map((item, i) => (
                                    <div
                                        key={i}
                                        className="issue-card"
                                        style={{ borderLeft: `3px solid var(--bs-${item.severityClass})` }}>
                                        <div
                                            className="issue-header"
                                            onClick={() => setOpenIssue(openIssue === i ? null : i)}>
                                            <div className="d-flex align-items-center gap-2 flex-1">
                                                <span
                                                    className={`badge bg-${item.severityClass} text-${
                                                        item.severityClass === 'warning' ? 'dark' : 'white'
                                                    }`}
                                                    style={{ fontSize: 10 }}>
                                                    {item.severity}
                                                </span>
                                                <span style={{ fontWeight: 500, fontSize: 13, color: '#333' }}>
                                                    {item.issue}
                                                </span>
                                            </div>
                                            <i
                                                className={`mdi ${
                                                    openIssue === i ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                                }`}
                                                style={{ color: '#adb5bd', fontSize: 12 }}
                                            />
                                        </div>
                                        {openIssue === i && (
                                            <div style={{ padding: '0 18px 14px' }}>
                                                {item.steps.map((step, j) => (
                                                    <div
                                                        key={j}
                                                        className="d-flex gap-2 align-items-start py-2"
                                                        style={{
                                                            borderBottom:
                                                                j < item.steps.length - 1
                                                                    ? '1px solid #f0f0f0'
                                                                    : 'none',
                                                        }}>
                                                        <div className="step-bubble" style={{ fontSize: 11 }}>
                                                            {j + 1}
                                                        </div>
                                                        <span style={{ fontSize: 13, color: '#6c757d', paddingTop: 4 }}>
                                                            {step}
                                                        </span>
                                                    </div>
                                                ))}
                                                <div
                                                    className="mt-2 p-2 rounded"
                                                    style={{
                                                        background: '#FFF8EC',
                                                        border: '1px solid #F9B74C',
                                                        fontSize: 12,
                                                        color: '#6c757d',
                                                    }}>
                                                    <i className="mdi mdi-email me-1" style={{ color: '#F9B74C' }} />
                                                    Still unresolved? Email{' '}
                                                    <a
                                                        href="mailto:support@rdpms.com"
                                                        style={{ color: '#F9B74C', fontWeight: 600 }}>
                                                        support@rdpms.com
                                                    </a>{' '}
                                                    with your system logs attached.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <div style={{ textAlign: 'center', color: '#adb5bd', fontSize: 12, marginTop: 28 }}>
                                    Copyright {currentYear} Hummingbird TekSystems, Inc. | CONFIDENTIAL
                                </div>
                            </div>
                        )}

                        {/* ══════════════════════════════════════ FAQ ══════════════════════════════════════ */}
                        {activeSection === 'faq' && (
                            <div className="container-fluid px-3 pt-3">
                                <div className="row align-items-center gx-2 gy-2 mb-3">
                                    <div className="col-12 col-md-auto">
                                        <h6 className="point-title fw-bold mb-0">Frequently Asked Questions</h6>
                                    </div>
                                </div>
                                {faqs.map((item, i) => (
                                    <div key={i} className="faq-item">
                                        <div className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                            <span style={{ flex: 1 }}>{item.q}</span>
                                            <i
                                                className={`mdi ${openFaq === i ? 'mdi-minus' : 'mdi-plus'}`}
                                                style={{ color: '#F9B74C', fontSize: 13, flexShrink: 0 }}
                                            />
                                        </div>
                                        {openFaq === i && <div className="faq-a">{item.a}</div>}
                                    </div>
                                ))}
                                <div className="report-alert-card mt-4 p-4 text-center">
                                    <i
                                        className="mdi mdi-headset"
                                        style={{ fontSize: 32, color: '#F9B74C', marginBottom: 10, display: 'block' }}
                                    />
                                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                                        Didn't find your answer?
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 16 }}>
                                        Our support team typically responds within 4 business hours.
                                    </div>
                                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                                        <button style={submitBtnStyle}>
                                            <i className="mdi mdi-email me-1" />
                                            Contact Support
                                        </button>
                                        <button style={outlineBtnStyle}>
                                            <i className="mdi mdi-chat-processing-outline me-1" />
                                            Live Chat
                                        </button>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', color: '#adb5bd', fontSize: 12, marginTop: 28 }}>
                                    Copyright {currentYear} Hummingbird TekSystems, Inc. | CONFIDENTIAL
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ════════════════ VIDEO MODAL ════════════════ */}
            {playingVideo && (
                <div className="modal-backdrop-custom" onClick={() => setPlayingVideo(null)}>
                    <div className="modal-box" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                marginBottom: 14,
                                gap: 12,
                            }}>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: '#333', marginBottom: 2 }}>
                                    {playingVideo.title}
                                </div>
                                <div style={{ fontSize: 12, color: '#6c757d' }}>{playingVideo.desc}</div>
                            </div>
                            <button
                                onClick={() => setPlayingVideo(null)}
                                style={{
                                    background: '#f5f5f5',
                                    border: 'none',
                                    borderRadius: 4,
                                    minWidth: 30,
                                    height: 30,
                                    cursor: 'pointer',
                                    fontSize: 18,
                                    flexShrink: 0,
                                }}>
                                ×
                            </button>
                        </div>
                        {playingVideo.videoUrl ? (
                            <video
                                key={playingVideo.videoUrl}
                                className="video-modal-player"
                                src={playingVideo.videoUrl}
                                controls
                                autoPlay
                                style={{
                                    width: '100%',
                                    borderRadius: 6,
                                    background: '#000',
                                    maxHeight: 360,
                                    display: 'block',
                                    marginBottom: 14,
                                }}
                            />
                        ) : (
                            <div
                                className="video-upload-zone mb-3"
                                onClick={() => videoInputRefs.current[playingVideo.id]?.click()}>
                                <i
                                    className="fas fa-cloud-upload-alt"
                                    style={{ fontSize: 36, color: '#F9B74C', marginBottom: 10, display: 'block' }}
                                />
                                <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 4 }}>
                                    No video uploaded yet
                                </div>
                                <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 12 }}>
                                    Click here to upload an MP4, WebM, or MOV file
                                </div>
                                <button style={{ ...outlineBtnStyle, fontSize: 12, padding: '6px 18px' }}>
                                    <i className="fas fa-upload me-1" />
                                    Choose Video File
                                </button>
                            </div>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 8,
                            }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span
                                    style={{
                                        fontSize: 11,
                                        background: '#FFF8EC',
                                        border: '1px solid #F9B74C',
                                        borderRadius: 20,
                                        padding: '2px 10px',
                                        color: '#333',
                                    }}>
                                    <i className="mdi mdi-clock-outline me-1" />
                                    {playingVideo.duration}
                                </span>
                                {playingVideo.videoUrl && (
                                    <span
                                        style={{
                                            fontSize: 11,
                                            background: '#e8f5e9',
                                            border: '1px solid #c8e6c9',
                                            borderRadius: 20,
                                            padding: '2px 10px',
                                            color: '#198754',
                                        }}>
                                        <i className="fas fa-check-circle me-1" />
                                        Video loaded
                                    </span>
                                )}
                            </div>
                            <div className="d-flex gap-2">
                                {playingVideo.videoUrl ? (
                                    <>
                                        <button
                                            style={{
                                                ...outlineBtnStyle,
                                                padding: '5px 14px',
                                                fontSize: 12,
                                                borderColor: '#adb5bd',
                                                color: '#6c757d',
                                            }}
                                            onClick={() => videoInputRefs.current[playingVideo.id]?.click()}>
                                            <i className="mdi mdi-sync me-1" />
                                            Replace
                                        </button>
                                        <a
                                            href={playingVideo.videoUrl}
                                            download={playingVideo.fileName || 'video.mp4'}
                                            style={{ textDecoration: 'none' }}>
                                            <button style={{ ...outlineBtnStyle, padding: '5px 14px', fontSize: 12 }}>
                                                <i className="mdi mdi-download" />
                                                Download
                                            </button>
                                        </a>
                                    </>
                                ) : (
                                    <button
                                        style={{ ...submitBtnStyle, padding: '5px 14px', fontSize: 12 }}
                                        onClick={() => videoInputRefs.current[playingVideo.id]?.click()}>
                                        <i className="mdi mdi-upload me-1" />
                                        Upload Video
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HelpDocumentation;
